// src/components/fix-actions/redesign/action-items-shell.tsx
//
// Redesign shell: DC module header + tab-aware agent thread + tab navigation.
// It is driven entirely by the page's existing state and handlers, so all
// orchestration (services, events, tracker) stays in fix-actions-page.tsx.
//
// For this checkpoint the Suggested Actions tab is fully redesigned; the
// Studio and Log tabs render the legacy components passed in as slots, so the
// module stays fully functional while those tabs are rebuilt.

import { useEffect, useMemo, useState } from "react";
import type { FixAction } from "../../../services/fix-actions/fix-actions-service";
import { Wrench } from "lucide-react";
import { ModuleHeader } from "../../shell/module-header";
import { AgentThread, type AgentThreadModel } from "./agent-thread";
import { SuggestedActionsView } from "./suggested-actions-view";
import { OptimizationStudioView } from "./optimization-studio-view";
import type { DetailCallbacks } from "./action-detail-panel";
import type { StrategyArtifact } from "../../../services/fix-actions/optimization-studio-service";
import "./action-items-redesign.css";

type Layout = "split" | "accordion";

export interface ActionItemsShellProps {
  brandName: string;
  activeTab: string;
  onTabChange: (t: string) => void;

  highPriorityCount: number;
  quickWinsCount: number;
  completedCount: number;

  actions: FixAction[];
  filterCategory: string;
  onFilterChange: (v: string) => void;
  searchQuery: string;
  onSearchChange: (v: string) => void;
  onGenerateAsset: DetailCallbacks["onGenerateAsset"];
  onExplainWithAI: (topic: string) => void;
  onNavigateToStudio?: (artifactId: string) => void;
  highlightActionTitle?: string | null;

  // Optimization Studio (redesigned) props
  onOpenArtifactTray: (artifact: StrategyArtifact) => void;
  optimisticArtifacts: StrategyArtifact[];
  brandId: string;
  highlightStudioArtifactId?: string | null;
  onClearStudioHighlight?: () => void;

  logSlot: React.ReactNode;
}

const TABS = [
  { k: "actions", label: "Suggested Actions" },
  { k: "content", label: "Optimization Studio" },
  { k: "insights", label: "Action Log" },
];

export function ActionItemsShell(props: ActionItemsShellProps) {
  const [ready, setReady] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState<number | null>(props.actions[0]?.id ?? null);
  const [layout, setLayout] = useState<Layout>("split");

  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setReady(true)));
    return () => cancelAnimationFrame(id);
  }, []);

  // ── move-mode agent thread from real data (no fabricated claims) ──
  const moveAction: FixAction | null = useMemo(() => {
    if (props.actions.length === 0) return null;
    const quick = props.actions
      .filter((a) => a.effort === "Low")
      .sort((a, b) => Number(b.impactScore ?? 0) - Number(a.impactScore ?? 0));
    if (quick[0]) return quick[0];
    return [...props.actions].sort((a, b) => Number(b.impactScore ?? 0) - Number(a.impactScore ?? 0))[0];
  }, [props.actions]);

  const thenActions = useMemo(() => {
    if (!moveAction) return [];
    return [...props.actions]
      .filter((a) => a.id !== moveAction.id)
      .sort((a, b) => Number(b.impactScore ?? 0) - Number(a.impactScore ?? 0))
      .slice(0, 2);
  }, [props.actions, moveAction]);

  const selectAction = (id: number) => {
    setSelectedActionId(id);
    setLayout("split");
    if (props.activeTab !== "actions") props.onTabChange("actions");
  };

  const agentModel: AgentThreadModel | null = useMemo(() => {
    if (!moveAction) return null;
    const n = moveAction.artifacts?.length ?? 0;
    return {
      mode: "move",
      status: `Reviewed ${props.actions.length} action${props.actions.length === 1 ? "" : "s"}`,
      lead: `${props.highPriorityCount} high-priority action${props.highPriorityCount === 1 ? "" : "s"} and ${props.quickWinsCount} quick win${props.quickWinsCount === 1 ? "" : "s"} are on your roadmap.`,
      rest: "Start with the highest-leverage move.",
      moveLabel: "Starting here",
      moveMeta: `${moveAction.strategicTheme || moveAction.category} · ${n} asset${n === 1 ? "" : "s"}`,
      moveTitle: moveAction.title,
      moveRationale: `${moveAction.effort} effort, ${moveAction.impactLevel} impact. I'll draft ${n} asset${n === 1 ? "" : "s"} for this action.`,
      moveCta: n > 0 ? `Draft ${n} asset${n === 1 ? "" : "s"}` : "Review action",
      onRunMove: () => {
        if (n > 0) {
          const first = moveAction.artifacts![0];
          props.onGenerateAsset(moveAction.id, moveAction.title, moveAction.strategicTheme || "", first.title, first.type, first.description || "", 0, n);
        } else {
          selectAction(moveAction.id);
        }
      },
      thenVerb: "Then I'll move to",
      thenChips: thenActions.map((a) => ({
        label: a.strategicTheme || a.title,
        onSelect: () => selectAction(a.id),
      })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveAction, thenActions, props.actions.length, props.highPriorityCount, props.quickWinsCount]);

  // Prod tab-button style, restored verbatim from the shipped design. Not
  // restyled: this is the module's internal nav and the prod design is the spec.
  const tabBtn = (active: boolean): React.CSSProperties => ({
    position: "relative",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "14px 0",
    fontSize: 16,
    fontWeight: active ? 600 : 500,
    color: active ? "var(--foreground)" : "var(--muted-foreground)",
  });

  return (
    // containerType makes 5cqi below resolve off this box, the same box the
    // ModuleHeader band measures against, so the band title and the page
    // content share one left edge. The maxWidth:1400 + auto margins that used
    // to wrap this were unique to Action Items and inset the whole module
    // further than any other page on a wide viewport (UPM-147).
    <div
      className="ai-redesign"
      style={{
        minHeight: "100vh",
        background: "var(--background)",
        color: "var(--foreground)",
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        lineHeight: 1.5,
        containerType: "inline-size",
      }}
    >
      <div>
        {/* module header (shared band) */}
        <ModuleHeader
          page="Action Items"
          icon={Wrench}
          title="Fix what moves the recommendation."
          description="Prioritized actions from your measurement, ranked by effort and impact. Each one targets a claim the models weigh."
        />

        {/* agent thread - actions tab only for this checkpoint */}
        {props.activeTab === "actions" && agentModel && <AgentThread model={agentModel} />}

        {/* tab nav — the module's internal nav, below the Upmode AI card.
            Prod layout and prod design, restored unchanged. */}
        <div style={{ marginTop: 14, padding: "0 var(--shell-gutter)", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 28 }}>
          {TABS.map((t) => {
            const active = props.activeTab === t.k;
            return (
              <button key={t.k} onClick={() => props.onTabChange(t.k)} style={tabBtn(active)}>
                {t.label}
                {active && <span style={{ position: "absolute", left: 0, right: 0, bottom: -1, height: 2, background: "var(--alpha-teal)", borderRadius: "2px 2px 0 0" }} />}
              </button>
            );
          })}
        </div>

        {/* body */}
        {props.activeTab === "actions" && (
          <SuggestedActionsView
            actions={props.actions}
            filterCategory={props.filterCategory}
            onFilterChange={props.onFilterChange}
            searchQuery={props.searchQuery}
            onSearchChange={props.onSearchChange}
            onGenerateAsset={props.onGenerateAsset}
            onExplainWithAI={props.onExplainWithAI}
            onNavigateToStudio={props.onNavigateToStudio}
            highlightActionTitle={props.highlightActionTitle}
            ready={ready}
            selectedId={selectedActionId}
            onSelectAction={setSelectedActionId}
            layout={layout}
            onLayoutChange={setLayout}
          />
        )}
        {props.activeTab === "content" && (
          <div style={{ padding: "8px var(--shell-gutter) 60px" }}>
            <OptimizationStudioView
              onOpenArtifactTray={props.onOpenArtifactTray}
              onExplainWithAI={props.onExplainWithAI}
              optimisticArtifacts={props.optimisticArtifacts}
              brandId={props.brandId}
              highlightArtifactId={props.highlightStudioArtifactId}
              onClearHighlight={props.onClearStudioHighlight}
            />
          </div>
        )}
        {props.activeTab === "insights" && <div style={{ padding: "8px var(--shell-gutter) 60px" }}>{props.logSlot}</div>}
      </div>
    </div>
  );
}
